
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import httpClient from "../../store/httpClient";
import { FileDto } from "../../dtos/ticket/fileDto";
import { getComplainTypes, IComplainType } from "../../dtos/ticket/IComplainType";
import { ComplainType } from "../../dtos/ticket/complainType";
import {
	getComplaintExpectationTypes,
	IComplaintExpectationType,
} from "../../dtos/ticket/IComplaintExpectationType";
import { ComplaintExpectationType } from "../../dtos/ticket/complaintExpectationType";
import { ComplaintDto } from "../../dtos/ticket/complaintDto";
import rules from "@/store/rules";
import { TicketState } from "@/dtos/ticket/ticketState";
import { TicketDto } from "@/dtos/ticket/ticketDto";
import { getZincCoatingText, ZincCoating } from "@/dtos/zincCoating";

@Component({
	components: {},
})
export default class EditTicketPositionComponent extends Vue {
	@Prop()
	private complaint!: ComplaintDto;
	@Prop()
	private ticket!: TicketDto;
	private valid: boolean = false;
	private formValid: boolean = false;
	private showGallery: boolean = false;
	private uploadingImages: boolean = false;
	private currentImage: number = 0;
	private source: any = "";
	private get expectationTypes(): IComplaintExpectationType[] {
		return getComplaintExpectationTypes();
	}
	private ersatzExpectation: ComplaintExpectationType = ComplaintExpectationType.Ersatzlieferung;
	private requiredRule = rules.requiredRule;

	private requiredExpectationDescription = [
		(value: string) =>
			this.complaint.expectationType === this.ersatzExpectation ||
			(value !== undefined && value !== null && value !== "") ||
			(this.$t("orderHistory.editTicketPosition.messages.requiredField") as string),
	];

	private requiredChoiceRule = [
		(value: ComplainType) =>
			value !== ComplainType.None ||
			(this.$t("orderHistory.editTicketPosition.messages.requiredField") as string),
	];

	private requiredNumberRule = [
		(value: number) =>
			!!value || (this.$t("orderHistory.editTicketPosition.messages.enterValue") as string),
		(value: number) =>
			value >= 1 || (this.$t("orderHistory.editTicketPosition.messages.minimumOne") as string),
		(value: number) =>
			value <= this.complaint.part.menge ||
			(this.$t("orderHistory.editTicketPosition.messages.maximum", {
				max: this.complaint.part.menge,
			}) as string),
	];

	private get beanstandungsTypen(): IComplainType[] {
		let filteredTypes = getComplainTypes().filter(
			(t) =>
				(t.value != ComplainType.PowderCoating ||
					this.complaint.part.powderCoatingTreatmentDto != null) &&
				(t.value != ComplainType.ZincCoating ||
					this.complaint.part.zincCoating != ZincCoating.None) &&
				(t.value != ComplainType.Feature || this.complaint.part.featureDtos.length > 0) &&
				(t.value != ComplainType.WrongOrMissingPressJoin || this.complaint.part.featureDtos.length > 0) &&
				(t.value != ComplainType.Weldline || this.complaint.part.weldlinesAsText != null)
		);
		// Put the "Andere Beanstandung" at the end
		filteredTypes = [
			...filteredTypes.filter((item) => item.value !== 8),
			...filteredTypes.filter((item) => item.value === 8),
		];
		return filteredTypes;
	}

	private beforeMount() {
		if (this.complaint !== null && this.complaint.numberOfWrongParts === 0) {
			this.complaint.numberOfWrongParts = 1;
		}
	}

	private mounted() {
		(this.$refs.form as any).validate();
	}

	@Watch("formValid")
	private onFormValidChanged() {
		this.validatePosition();
	}

	private onExpectationTypeChanged() {
		if (!this.complaint.expectation) {
			this.complaint.expectation = "";
		}
		this.validatePosition();
		this.$emit("changed");
	}

	private get partsAreMissing() {
		const result: boolean = this.complaint.complainType === ComplainType.Missing;
		return result;
	}

	private async uploadFiles(files: FileList) {
		if (files.length === 0) {
			return;
		}
		if (files.length + this.complaint.files.length > 10) {
			this.$store.commit(
				"setSnackbarErrorText",
				this.$t("orderHistory.editTicketPosition.messages.maxImagesExceeded")
			);
			return;
		}
		this.uploadingImages = true;
		await this.$nextTick();
		for (const file of files) {
			const data = new FormData();
			data.append("file", file);
			const config = {
				header: {
					"Content-Type": "multipart/form-data",
				},
				onUploadProgress: (progressEvent: any) => {
					const percentCompleted = Math.round(
						(progressEvent.loaded * 100) / progressEvent.total
					);
				},
			};

			try {
				const response = await httpClient().post("Ticket/UploadImage", data, config);
				const newImage: FileDto = new FileDto();
				newImage.guid = response.data;
				this.complaint.files.push(newImage);
			} catch {
				this.$store.commit(
					"setSnackbarErrorText",
					this.$t("orderHistory.editTicketPosition.messages.imageUploadError")
				);
			}
		}

		this.uploadingImages = false;
		this.validatePosition();
	}

	private onImageClick(index: number) {
		this.currentImage = index;
		this.showGallery = true;
	}

	private deleteImage(index: number) {
		this.complaint.files.splice(index, 1);
	}

	private getThumbnailUrl(articleId: string) {
		return this.$store.getters.getThumbnailUrl(articleId, undefined);
	}

	private getComplaintImageUri(guid: string) {
		return process.env.VUE_APP_HELPDESK_BACKEND + "api/File/GetImage?guid=" + guid;
	}

	private setMaxParts() {
		this.complaint.numberOfWrongParts = this.complaint.part.menge;
	}

	private get isReadonly() {
		return this.ticket.state != TicketState.Open;
	}

	private validatePosition() {
		this.formValid = (this.$refs.form as any).validate();
		if (this.partsAreMissing) {
			this.valid = this.formValid;
		} else {
			this.valid = this.formValid && this.complaint.files && this.complaint.files.length > 0;
		}
		this.$emit("validChanged", this.valid);
	}

	private getZincCoatingText(zincCoating: any): string {
		return getZincCoatingText(zincCoating);
	}

	private getFinishText(value: number) {
		return this.$store.getters.getPowdercoatingFinishText(value);
	}

	private getStructureText(value: number) {
		return this.$store.getters.getPowdercoatingStructureText(value);
	}
}
